import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUp, ArrowLeft, Hammer, Sparkles, CheckCircle2, Heart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";

const SECTIONS = [
    { status: "in_progress", label: "in the works", icon: Hammer, tint: "text-terracotta" },
    { status: "planned", label: "up next", icon: Sparkles, tint: "text-sage" },
    { status: "fixed", label: "shipped", icon: CheckCircle2, tint: "text-inkSoft" },
];

export default function Board() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [wall, setWall] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/board")
            .then(r => setItems(r.data))
            .catch(() => toast.error("couldn't load the board"))
            .finally(() => setLoading(false));
        api.get("/board/wall").then(r => setWall(r.data)).catch(() => {});
    }, []);

    const vote = async (id) => {
        if (!user) {
            toast("sign in to upvote", { description: "your vote helps us pick what to build next." });
            return;
        }
        try {
            const r = await api.post(`/board/${id}/vote`);
            setItems(prev => prev.map(it => it.id === id ? { ...it, votes: r.data.votes, voted: r.data.voted } : it));
        } catch {
            toast.error("couldn't register that vote");
        }
    };

    return (
        <div className="relative z-10 min-h-screen px-4 sm:px-6 md:px-12 pb-20" data-testid="board-page">
            <header className="py-6 flex items-center justify-between max-w-3xl mx-auto">
                <Link to={user ? "/companion" : "/"} className="flex items-center gap-2 text-inkSoft hover:text-ink font-body text-sm" data-testid="board-back">
                    <ArrowLeft className="w-4 h-4" /> back
                </Link>
                <Link to="/" className="flex items-center gap-2">
                    <img src={BRAND.logo} alt="" className="w-6 h-6 object-contain" />
                    <span className="font-heading text-xl text-ink">{BRAND.handle}</span>
                </Link>
            </header>

            <div className="max-w-3xl mx-auto">
                <p className="text-xs uppercase tracking-[0.3em] text-inkSoft mb-3 font-body">beta board</p>
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink mb-3">what we're fixing</h1>
                <p className="text-inkSoft font-body mb-10 max-w-lg">
                    pax is in public beta. this is the honest list — what's being built, what's next, and what already shipped. upvote what matters to you.
                </p>

                {loading && <p className="text-inkSoft italic font-body">loading…</p>}

                {SECTIONS.map(({ status, label, icon: Icon, tint }) => {
                    const rows = items.filter(i => i.status === status);
                    if (!rows.length) return null;
                    return (
                        <section key={status} className="mb-10" data-testid={`board-section-${status}`}>
                            <h2 className={`flex items-center gap-2 font-body text-sm uppercase tracking-widest mb-4 ${tint}`}>
                                <Icon className="w-4 h-4" /> {label}
                            </h2>
                            <div className="space-y-2.5">
                                {rows.map((it, idx) => (
                                    <motion.div
                                        key={it.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.04 }}
                                        className="flex items-center gap-4 bg-white/70 border border-clayHover rounded-2xl px-5 py-4 shadow-soft"
                                        data-testid="board-item"
                                    >
                                        <button
                                            onClick={() => vote(it.id)}
                                            data-testid={`board-vote-${it.id}`}
                                            className={`flex flex-col items-center gap-0.5 min-w-[44px] rounded-xl px-2 py-1.5 border transition-colors font-body text-xs ${
                                                it.voted
                                                    ? "bg-terracotta/15 border-terracotta/40 text-terracotta"
                                                    : "bg-clay/50 border-transparent text-inkSoft hover:border-terracotta/40 hover:text-terracotta"
                                            }`}
                                            aria-label="Upvote"
                                        >
                                            <ArrowUp className="w-3.5 h-3.5" />
                                            {it.votes}
                                        </button>
                                        <p className={`font-body text-ink ${status === "fixed" ? "opacity-60" : ""}`}>{it.title}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </section>
                    );
                })}

                {wall.length > 0 && (
                    <section className="mt-14 pt-10 border-t border-clayHover" data-testid="thank-you-wall">
                        <h2 className="flex items-center gap-2 font-body text-sm uppercase tracking-widest mb-2 text-terracotta">
                            <Heart className="w-4 h-4" /> thank you wall
                        </h2>
                        <p className="text-inkSoft font-body text-sm mb-5 max-w-md">
                            every note sent through the feedback button shapes pax. these beta testers made it better:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {wall.map((t, i) => (
                                <motion.span
                                    key={`${t.name}-${i}`}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: i * 0.04 }}
                                    className="inline-flex items-center gap-1.5 bg-white/70 border border-clayHover rounded-full px-4 py-1.5 font-body text-sm text-ink shadow-soft"
                                    data-testid="wall-chip"
                                >
                                    <Heart className="w-3 h-3 text-terracotta fill-terracotta/60" />
                                    {t.name}
                                    {t.count > 1 && <span className="text-xs text-inkSoft">×{t.count}</span>}
                                </motion.span>
                            ))}
                        </div>
                        <p className="text-xs text-inkSoft/70 font-body mt-4">
                            want your name here? send a note via the feedback button inside the app.
                        </p>
                    </section>
                )}
            </div>
        </div>
    );
}
